import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Header({ onOpenModal }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [headerSettings, setHeaderSettings] = useState({
    logo_url: 'https://customer-assets.emergentagent.com/job_e8fbeb9b-6487-4b78-a6f7-665b46ef2f3a/artifacts/bk3s60pn_Circuit%207%20buloke%20logo%20%282%29.png',
    navigation_links: []
  });
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchHeaderSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/header-settings`);
        if (response.data) {
          // Convert relative logo URL to full URL
          const settings = response.data;
          if (settings.logo_url && !settings.logo_url.startsWith('http')) {
            settings.logo_url = `${API_URL}${settings.logo_url}`;
          }
          setHeaderSettings(settings);
        }
      } catch (error) {
        console.error('Error fetching header settings:', error);
      }
    };
    fetchHeaderSettings();
  }, []);

  // Filter only visible navigation links
  const menuItems = headerSettings.navigation_links
    ? headerSettings.navigation_links
        .filter(link => link.is_visible !== false)
        .map(link => ({
          name: link.label,
          path: link.url,
          external: link.is_external === true
        }))
    : [];

  const handleMenuClick = (item) => {
    if (item.external) {
      window.open(item.path, '_blank');
    } else if (item.path.includes('#')) {
      const section = item.path.split('#')[1];
      navigate('/');
      setTimeout(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      navigate(item.path);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header
        data-testid="header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'glass shadow-md' : 'bg-white'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src={headerSettings.logo_url} 
                alt="The Circuit Logo" 
                className="h-16"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {menuItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleMenuClick(item)}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="text-[#222] hover:text-[#E12429] font-medium transition-colors relative group"
                >
                  {item.name}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#E12429] transition-all group-hover:w-full"></span>
                </button>
              ))}
            </nav>

            {/* CTA Button */}
            <button
              onClick={() => onOpenModal()}
              data-testid="header-cta-button"
              className="hidden lg:block btn-primary"
            >
              Get In Touch
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-button"
              className="lg:hidden p-2"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-white z-40 lg:hidden"
          style={{ top: '80px' }}
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col items-center py-8 space-y-6">
            {menuItems.map((item) => (
              <button
                key={item.name}
                onClick={() => handleMenuClick(item)}
                className="text-lg font-medium text-[#222] hover:text-[#E12429] transition-colors"
              >
                {item.name}
              </button>
            ))}
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                onOpenModal();
              }}
              className="btn-primary mt-4"
            >
              Get In Touch
            </button>
          </nav>
        </div>
      )}
    </>
  );
}